import React from 'react';
import _ from 'lodash';

import {connect} from 'react-redux';

import Label from 'components/Label';
import Select from 'components/Select';
import CheckBox from 'components/CheckBox';
import DateInput from 'components/DateInput';
import MoneyInput from 'components/MoneyInput';
import ErrorBlock from 'components/ErrorBlock';

import {
  editDepositSummaryAmountToBeRetainedPaid,
  editDepositSummaryRefundToBeIssuedPaid,
  editDepositReleaseDueDate,
  editDepositSummaryDepositType,
  fetchDepositSummary,
  fetchDepositTypes,
  fetchRoomChecksTimeline,
  selectDepositTypes,
  MODALS
} from 'ducks/bookingSets';
import {storeDataInModal, selectModal} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {formatDate} from 'utils/datetime';
import {formatDateForApi} from 'utils/datetime';

import './styles.css';

interface DepositSummaryCheckProps {
  depositTypeName: string;
  depositTypeId: number;
  amountToBeRetained: string;
  refundToBeIssued: string;
  amountToBeRetainedPaid: boolean;
  refundToBeIssuedPaid: boolean;
  depositReleaseDueDate: Date;
}

interface DepositSummaryDetailsProps {
  depositSummary: DepositSummaryCheckProps;
  bookingSetId: number;
  editDepositSummaryAmountToBeRetainedPaid: any;
  editDepositSummaryRefundToBeIssuedPaid: any;
  editDepositReleaseDueDate: any;
  editDepositSummaryDepositType: any;
  storeDataInModal: any;
  depositReleaseDueDateError: any;
  fetchDepositTypes: any;
  depositTypes: any;
  requiresPermission: any;
  locationRef: string;
}

export const buildDepositTypeOption = depositType => ({
  value: depositType.id,
  label: depositType.name
});

class DepositSummaryDetails extends React.Component<
  DepositSummaryDetailsProps
> {
  componentDidMount() {
    this.props.fetchDepositTypes();
  }
  onAmountToBeRetainedPaidChange = event => {
    const newAmountToBeRetainedPaidValue = event.target.checked;

    const {bookingSetId, locationRef} = this.props;

    const extraActions = [
      () => fetchDepositSummary({bookingSetId}),
      () => fetchRoomChecksTimeline({bookingSetId})
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_DEPOSIT_SUMMARY,
        locationRef
      },
      () => {
        this.props.editDepositSummaryAmountToBeRetainedPaid(
          {
            bookingSetId,
            newAmountToBeRetainedPaid: newAmountToBeRetainedPaidValue
          },
          extraActions
        );
      }
    );
  };

  onRefundToBeIssuedPaidChange = event => {
    const newRefundToBeIssuedPaidValue = event.target.checked;

    const {bookingSetId, locationRef} = this.props;

    const extraActions = [
      () => fetchDepositSummary({bookingSetId}),
      () => fetchRoomChecksTimeline({bookingSetId})
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_DEPOSIT_SUMMARY,
        locationRef
      },
      () => {
        this.props.editDepositSummaryRefundToBeIssuedPaid(
          {
            bookingSetId,
            newRefundToBeIssuedPaid: newRefundToBeIssuedPaidValue
          },
          extraActions
        );
      }
    );
  };

  onDepositReleaseDueDateChange = newDepositReleaseDueDateValue => {
    const {bookingSetId, locationRef} = this.props;

    const extraActions = [
      () => fetchDepositSummary({bookingSetId}),
      () =>
        this.props.storeDataInModal({
          modalName: MODALS.DEPOSIT_SUMMARY_DETAIL_MODAL,
          data: {
            depositReleaseDueDateError: null
          }
        })
    ];

    const failureExtraActions = [
      ({errors}) => {
        return this.props.storeDataInModal({
          modalName: MODALS.DEPOSIT_SUMMARY_DETAIL_MODAL,
          data: {
            depositReleaseDueDateError: _.get(
              errors,
              'newDepositReleaseDueDate',
              null
            )
          }
        });
      }
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_DEPOSIT_SUMMARY,
        locationRef
      },
      () => {
        this.props.editDepositReleaseDueDate(
          {
            bookingSetId,
            newDepositReleaseDueDate: formatDateForApi(
              newDepositReleaseDueDateValue
            )
          },
          extraActions,
          failureExtraActions
        );
      }
    );
  };

  onDepositTypeChange = option => {
    const newDepositTypeId = option.value;

    const {bookingSetId, locationRef} = this.props;

    const extraActions = [() => fetchDepositSummary({bookingSetId})];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_DEPOSIT_SUMMARY,
        locationRef
      },
      () => {
        this.props.editDepositSummaryDepositType(
          {
            bookingSetId,
            newDepositType: newDepositTypeId
          },
          extraActions
        );
      }
    );
  };

  render() {
    const {
      depositSummary,
      depositReleaseDueDateError = null,
      depositTypes
    } = this.props;

    const depositType = {
      id: depositSummary.depositTypeId,
      name: depositSummary.depositTypeName
    };

    return (
      <div className="DepositSummaryDetails">
        <div className="label">
          <Label>Deposit type</Label>
        </div>
        <Select
          className={depositTypes}
          options={[...depositTypes.map(buildDepositTypeOption)]}
          onChange={this.onDepositTypeChange}
          value={buildDepositTypeOption(depositType)}
          clearable={false}
        />
        <div className="label">
          <Label>Amount to be retained</Label>
        </div>
        <MoneyInput
          disabled={true}
          defaultValue={depositSummary.amountToBeRetained}
        />
        <CheckBox
          name="paidAmountToBeRetained"
          label="Paid"
          value={depositSummary.amountToBeRetainedPaid}
          onChange={this.onAmountToBeRetainedPaidChange}
        />
        <div className="label">
          <Label>Refund to be issued</Label>
        </div>
        <MoneyInput
          disabled={true}
          defaultValue={depositSummary.refundToBeIssued}
        />
        <CheckBox
          name="paidRefundToBeIssued"
          label="Paid"
          value={depositSummary.refundToBeIssuedPaid}
          onChange={this.onRefundToBeIssuedPaidChange}
        />
        <div className="label">
          <Label>Deposit release due date </Label>
        </div>
        <DateInput
          onChange={this.onDepositReleaseDueDateChange}
          value={
            depositSummary.depositReleaseDueDate
              ? formatDate(depositSummary.depositReleaseDueDate)
              : undefined
          }
        />
        {!_.isNull(depositReleaseDueDateError) && (
          <ErrorBlock>{depositReleaseDueDateError}</ErrorBlock>
        )}
      </div>
    );
  }
}

const mapStateToModalData = state => {
  const modal = selectModal(state, MODALS.DEPOSIT_SUMMARY_DETAIL_MODAL);
  const {depositReleaseDueDateError} = _.get(modal, 'data');
  const depositTypes = selectDepositTypes(state);

  return {
    depositReleaseDueDateError,
    depositTypes
  };
};

const mapDispatchToProps = {
  editDepositSummaryAmountToBeRetainedPaid,
  editDepositSummaryRefundToBeIssuedPaid,
  editDepositReleaseDueDate,
  editDepositSummaryDepositType,
  storeDataInModal,
  fetchDepositTypes,
  requiresPermission
};

export default connect(mapStateToModalData, mapDispatchToProps)(
  DepositSummaryDetails
);
